// GenBulkStrategyMonitor.jsx - WITH SEARCH & DEPENDENCY FILTERS
// + OPTIMISTIC STAT CARD UPDATES (Open count syncs with notification cards)
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Stack, Chip, IconButton, Button, Grid,
  CircularProgress, Tooltip, Typography, alpha, Avatar,
  Collapse, Alert, AlertTitle, ToggleButtonGroup, ToggleButton, Badge,
  TextField, InputAdornment, Menu, MenuItem, Divider
} from '@mui/material';
import {
  History as HistoryIcon, Timeline as TimelineIcon,
  Refresh as RefreshIcon, Circle as DotIcon,
  Dashboard as DashboardIcon, Bolt as BoltIcon,
  CloudDone as CloudDoneIcon, Speed as SpeedIcon, Sync as SyncIcon,
  CheckCircle as CheckCircleIcon, Warning as WarningIcon,
  Storage as StorageIcon, CloudOff as CloudOffIcon, WifiOff as WifiOffIcon,
  Schedule as ScheduleIcon, Search as SearchIcon, FilterList as FilterListIcon,
  Clear as ClearIcon, Link as LinkIcon, LinkOff as LinkOffIcon,
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon
} from '@mui/icons-material';

import { BulkStrategyCard } from './components/BulkStrategyCard';
import { StatCard } from './components/StatCard';
import { DataCache, RequestManager } from './utils/cache';
import { StrategyApiService } from './services/strategyApi';
import { useWebSocket } from './hooks/useWebSocket';

const GenBulkStrategyMonitor = ({ 
  apiBaseUrl = window.location.origin,
  onInitialLoadComplete,
  onLoadError 
}) => {
  // ==================== NAVIGATION ====================
  const navigate = useNavigate();
  
  // ==================== STATE ====================
  const [bulkStrategies, setBulkStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [timeframeFilter, setTimeframeFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState(false);
  const [statusCounts, setStatusCounts] = useState(null);
  const [strategyBroadcasts, setStrategyBroadcasts] = useState({});
  const [broadcastsLoading, setBroadcastsLoading] = useState({});
  const [strategyTrades, setStrategyTrades] = useState({});
  const [tradesLoading, setTradesLoading] = useState({});
  const [isOffline, setIsOffline] = useState(false);
  const [nyCurrentTime, setNyCurrentTime] = useState('');
  const [analyzerSummary, setAnalyzerSummary] = useState(null);

  // ==================== SEARCH & FILTER STATE ====================
  const [searchQuery, setSearchQuery] = useState('');
  const [strategyTypeFilter, setStrategyTypeFilter] = useState('all'); // 'all', 'g-only', 'non-g'
  const [dependencyFilter, setDependencyFilter] = useState('all'); // 'all', 'with-dep', 'no-dep'
  const [directionFilter, setDirectionFilter] = useState('all'); // 'all', 'LONG', 'SHORT'
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // ==================== SERVICE REFS ====================
  const dataCache = useRef(new DataCache(60000));
  const requestManager = useRef(new RequestManager());
  const apiService = useRef(null);
  const wsUpdateTimeoutRef = useRef(null);
  const lastSuccessfulLoadRef = useRef(null);
  const initialLoadRef = useRef(false);
  const mountedRef = useRef(true);

  // ==================== PERFORMANCE TIMING REFS ====================
  const componentMountTime = useRef(null);
  const loadStartTime = useRef(null);
  const wsMessageTimes = useRef([]);
  const stateUpdateTimes = useRef([]);

  // ==================== COMPONENT MOUNT TIMING ====================
  useEffect(() => {
    componentMountTime.current = performance.now();
    console.log('[MONITOR-PERF] 🎬 Component mounted at', new Date().toISOString());
    
    return () => {
      const totalLifetime = performance.now() - componentMountTime.current;
      console.log(`[MONITOR-PERF] 🏁 Component unmounted after ${totalLifetime.toFixed(2)}ms`);
      
      // Performance summary on unmount
      if (wsMessageTimes.current.length > 0) {
        const avgWsTime = wsMessageTimes.current.reduce((a, b) => a + b, 0) / wsMessageTimes.current.length;
        console.log(`[MONITOR-PERF] 📊 WS Messages: ${wsMessageTimes.current.length}, Avg: ${avgWsTime.toFixed(2)}ms`);
      }
      
      mountedRef.current = false;
    };
  }, []);

  // ==================== API SERVICE INIT ====================
  useEffect(() => {
    const initStartTime = performance.now();
    
    apiService.current = new StrategyApiService(
      apiBaseUrl,
      requestManager.current,
      dataCache.current
    );
    
    const initTime = performance.now() - initStartTime;
    console.log(`[MONITOR-PERF] 🔧 API Service initialized in ${initTime.toFixed(2)}ms`);
  }, [apiBaseUrl]);

  // ==================== CLEANUP ====================
  useEffect(() => {
    return () => {
      if (wsUpdateTimeoutRef.current) {
        clearTimeout(wsUpdateTimeoutRef.current);
      }
    };
  }, []);

  // ==================== INITIAL LOAD COMPLETE ====================
  useEffect(() => {
    if (!loading && !initialLoadRef.current && bulkStrategies.length > 0) {
      initialLoadRef.current = true;
      const initTime = performance.now() - componentMountTime.current;
      console.log(`[MONITOR-PERF] ✅ Initial load complete in ${initTime.toFixed(2)}ms`);
      onInitialLoadComplete?.();
    }
  }, [loading, bulkStrategies.length, onInitialLoadComplete]);

  // ==================== NY TIME CLOCK ====================
  useEffect(() => {
    const updateNYTime = () => {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZoneName: 'short',
        });
        setNyCurrentTime(formatter.format(new Date()));
      } catch (error) {
        console.error('Error formatting NY time:', error);
      }
    };

    updateNYTime();
    const interval = setInterval(updateNYTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // ==================== ADD LIVE UPDATE ====================
  const addLiveUpdate = useCallback((message, type, data = {}) => {
    if (!mountedRef.current) return;

    const strategyId = data.strategy_id || data.bulk_strategy_id || 'SYSTEM';

    const update = {
      id: Date.now() + Math.random(),
      message: message || 'Update received',
      type: type || 'info',
      timestamp: new Date(),
      strategy_id: strategyId,
      bulk_strategy_id: strategyId,
      symbol: data.symbol,
      signal: data.signal,
      timeframe: data.timeframe,
      raw: data,
    };

    setLiveUpdates(prev => [update, ...prev].slice(0, 50));
  }, []);

  // ==================== UPDATE STRATEGY FROM WEBSOCKET ====================
  const updateStrategyFromWebSocket = useCallback((wsData) => {
    if (!mountedRef.current) return;

    const wsStartTime = performance.now();
    const messageType = wsData.type;

    console.log(`[WS-UPDATE] Received: ${messageType}`, wsData);

    // ========== HANDLE STATE_SYNC (All strategies broadcast) ==========
    if (messageType === 'state_sync') {
      const activeStrategies = wsData.active_strategies || [];
      const summary = wsData.summary || {};

      console.log(`[WS-UPDATE] state_sync - ${activeStrategies.length} active strategies`);

      if (apiService.current && activeStrategies.length > 0) {
        const transformed = apiService.current.transformAnalyzerResponse({
          summary: summary,
          active_strategies: activeStrategies,
          timestamp: wsData.timestamp,
        });

        if (transformed?.strategies) {
          setBulkStrategies(prev => {
            const updatedMap = new Map();

            // Keep existing strategies
            (Array.isArray(prev) ? prev : []).forEach(s => {
              updatedMap.set(s.bulk_strategy_id, s);
            });

            // Update with new data
            transformed.strategies.forEach(s => {
              const existing = updatedMap.get(s.bulk_strategy_id);
              if (existing) {
                updatedMap.set(s.bulk_strategy_id, {
                  ...existing,
                  ...s,
                  current_state: { ...existing.current_state, ...s.current_state },
                  progress: s.progress,
                });
              } else {
                updatedMap.set(s.bulk_strategy_id, s);
              }
            });

            return Array.from(updatedMap.values());
          });

          setStatusCounts(transformed.statusCounts);
          setAnalyzerSummary(summary);
        }
      }

      addLiveUpdate(
        `📊 State sync: ${summary.pending || 0} pending, ${summary.open || 0} open`,
        'state_sync',
        wsData
      );

      const totalTime = performance.now() - wsStartTime;
      wsMessageTimes.current.push(totalTime);
      console.log(`[MONITOR-PERF] 📨 state_sync: ${totalTime.toFixed(2)}ms`);
      return;
    }

    // ========== HANDLE PENDING_UPDATE ==========
    if (messageType === 'pending_update') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];

        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          const completionPct = wsData.completion_percentage || 0;
          const totalSubs = strategy.sub_strategies?.length || 1;
          const completedSubs = Math.round((completionPct / 100) * totalSubs);

          return {
            ...strategy,
            current_state: {
              ...strategy.current_state,
              status: 'pending',
              in_position: false,
              trigger_signal: wsData.trigger_signal,
              pending_confirmations: wsData.pending_confirmations || [],
            },
            progress: {
              completed: completedSubs,
              total: totalSubs,
              percentage: completionPct,
            },
          };
        });
      });

      addLiveUpdate(
        `⏳ ${strategyId}: ${wsData.completion_percentage}% complete`,
        'pending_update',
        wsData
      );

      const totalTime = performance.now() - wsStartTime;
      wsMessageTimes.current.push(totalTime);
      console.log(`[MONITOR-PERF] 📨 pending_update: ${totalTime.toFixed(2)}ms`);
      return;
    }

    // ========== HANDLE BULK_ENTRY ==========
    if (messageType === 'bulk_entry') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];

        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          return {
            ...strategy,
            current_state: {
              ...strategy.current_state,
              status: 'open',
              in_position: true,
              entry_time: wsData.entry_time,
              entry_price: wsData.entry_price,
              confirmations: wsData.confirmations,
            },
            progress: {
              completed: strategy.sub_strategies?.length || 1,
              total: strategy.sub_strategies?.length || 1,
              percentage: 100,
            },
          };
        });
      });

      // ✅ FIX: Optimistically update summary counts so stat cards update instantly
      // This ensures the "Open" stat card shows 1 at the same time as the notification card
      setAnalyzerSummary(prev => prev ? {
        ...prev,
        open: (prev.open || 0) + 1,
        pending: Math.max(0, (prev.pending || 0) - 1),
      } : prev);

      // Clear cached trades for this strategy
      setStrategyTrades(prev => {
        const newTrades = { ...prev };
        delete newTrades[strategyId];
        return newTrades;
      });

      addLiveUpdate(
        `🟢 ENTRY: ${strategyId} @ $${wsData.entry_price}`,
        'bulk_entry',
        wsData
      );

      const totalTime = performance.now() - wsStartTime;
      wsMessageTimes.current.push(totalTime);
      console.log(`[MONITOR-PERF] 📨 bulk_entry: ${totalTime.toFixed(2)}ms`);
      return;
    }

    // ========== HANDLE BULK_EXIT ==========
    if (messageType === 'bulk_exit') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];

        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          // Reset all sub-strategies to incomplete
          const resetSubStates = {};
          Object.keys(strategy.current_state?.all_sub_strategies_state || {}).forEach(subId => {
            resetSubStates[subId] = {
              ...strategy.current_state.all_sub_strategies_state[subId],
              completed: false,
            };
          });

          return {
            ...strategy,
            current_state: {
              ...strategy.current_state,
              status: 'completed',
              in_position: false,
              exit_time: wsData.exit_time,
              exit_price: wsData.exit_price,
              pnl: wsData.pnl,
              pnl_pct: wsData.pnl_pct,
              all_sub_strategies_state: resetSubStates,
            },
            progress: {
              completed: 0,
              total: strategy.sub_strategies?.length || 1,
              percentage: 0,
            },
            total_complete_trades: (strategy.total_complete_trades || 0) + 1,
          };
        });
      });

      // ✅ FIX: Optimistically update summary counts so stat cards update instantly
      // This ensures the "Open" stat card shows 0 at the same time as the exit notification card
      setAnalyzerSummary(prev => prev ? {
        ...prev,
        open: Math.max(0, (prev.open || 0) - 1),
      } : prev);

      // Clear cached trades
      setStrategyTrades(prev => {
        const newTrades = { ...prev };
        delete newTrades[strategyId];
        return newTrades;
      });

      const pnlSign = wsData.pnl >= 0 ? '+' : '';
      const pnlColor = wsData.pnl >= 0 ? '🟢' : '🔴';
      addLiveUpdate(
        `${pnlColor} EXIT: ${strategyId} @ $${wsData.exit_price} | P&L: ${pnlSign}${wsData.pnl?.toFixed(2)}`,
        'bulk_exit',
        wsData
      );

      const totalTime = performance.now() - wsStartTime;
      wsMessageTimes.current.push(totalTime);
      console.log(`[MONITOR-PERF] 📨 bulk_exit: ${totalTime.toFixed(2)}ms`);
      return;
    }

    // ========== HANDLE ANALYSIS_COMPLETE ==========
    if (messageType === 'analysis_complete') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];

        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          return {
            ...strategy,
            summary: wsData.summary,
            last_analysis: wsData.timestamp,
          };
        });
      });

      addLiveUpdate(
        `📊 Analysis: ${strategyId} - ${wsData.summary?.total_trades || 0} trades`,
        'analysis_complete',
        wsData
      );

      const totalTime = performance.now() - wsStartTime;
      wsMessageTimes.current.push(totalTime);
      console.log(`[MONITOR-PERF] 📨 analysis_complete: ${totalTime.toFixed(2)}ms`);
      return;
    }

    // ========== HANDLE STRATEGY_UPDATE (Generic) ==========
    if (messageType === 'strategy_update') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];

        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          const updatedState = { ...strategy.current_state };

          if (wsData.status) updatedState.status = wsData.status;
          if (wsData.entry_time) updatedState.entry_time = wsData.entry_time;
          if (wsData.entry_price) updatedState.entry_price = wsData.entry_price;
          if (wsData.confirmations) updatedState.confirmations = wsData.confirmations;

          const completionPct = wsData.completion_percentage ?? strategy.progress?.percentage ?? 0;
          const totalSubs = strategy.sub_strategies?.length || 1;
          const completedSubs = Math.round((completionPct / 100) * totalSubs);

          return {
            ...strategy,
            current_state: updatedState,
            progress: {
              completed: completedSubs,
              total: totalSubs,
              percentage: completionPct,
            },
          };
        });
      });

      addLiveUpdate(
        `📡 ${strategyId}: ${wsData.status || 'updated'}`,
        'strategy_update',
        wsData
      );

      const totalTime = performance.now() - wsStartTime;
      wsMessageTimes.current.push(totalTime);
      console.log(`[MONITOR-PERF] 📨 strategy_update: ${totalTime.toFixed(2)}ms`);
      return;
    }

    // ========== HANDLE SIGNAL_UPDATE (Raw signal) ==========
    if (messageType === 'signal_update') {
      addLiveUpdate(
        `📶 Signal: ${wsData.signal || wsData.data?.signal || 'unknown'}`,
        'signal_update',
        wsData
      );
      return;
    }

    // Log unhandled message types
    console.log(`[WS-UPDATE] Unhandled message type: ${messageType}`);

  }, [addLiveUpdate]);

  // ==================== WEBSOCKET CONNECTION ====================
  const { isConnected, reconnect: wsReconnect } = useWebSocket(apiBaseUrl, (wsData) => {
    const messageType = wsData.type || 'unknown';

    // Skip connection/pong messages
    if (messageType === 'connection' || messageType === 'pong') {
      return;
    }

    // Skip initial_data (handled separately if needed)
    if (messageType === 'initial_data') {
      console.log('[WS] Received initial_data, skipping...');
      return;
    }

    updateStrategyFromWebSocket(wsData);
  });

  // ==================== LOAD CURRENT STATE FROM ANALYZER ====================
  const loadCurrentState = useCallback(async (broadcast = true) => {
    if (!apiService.current || !mountedRef.current) return;

    console.log('[MONITOR-PERF] 🚀 Loading current state from analyzer...');
    loadStartTime.current = performance.now();

    try {
      const result = await apiService.current.loadAllStrategiesCurrentState(broadcast);

      if (!mountedRef.current) return;

      if (result?.strategies) {
        setBulkStrategies(prev => {
          const updatedMap = new Map();

          // Keep existing strategies (for ones not in analyzer response)
          (Array.isArray(prev) ? prev : []).forEach(s => {
            updatedMap.set(s.bulk_strategy_id, s);
          });

          // Update/add from analyzer
          result.strategies.forEach(s => {
            const existing = updatedMap.get(s.bulk_strategy_id);
            if (existing) {
              updatedMap.set(s.bulk_strategy_id, {
                ...existing,
                ...s,
                current_state: { ...existing.current_state, ...s.current_state },
                progress: s.progress,
              });
            } else {
              updatedMap.set(s.bulk_strategy_id, s);
            }
          });

          return Array.from(updatedMap.values());
        });

        setStatusCounts(result.statusCounts);
        setAnalyzerSummary(result.summary);
        setIsOffline(false);

        lastSuccessfulLoadRef.current = {
          strategies: result.strategies,
          statusCounts: result.statusCounts,
          timestamp: new Date(),
        };

        const totalTime = performance.now() - loadStartTime.current;
        console.log(`[MONITOR-PERF] ✅ Current state loaded: ${totalTime.toFixed(2)}ms (${result.strategies.length} strategies)`);

        return result;
      }
    } catch (error) {
      if (!mountedRef.current) return;

      console.error('[MONITOR-PERF] ❌ Current state load failed:', error);

      const isNetworkError = error.message.includes('fetch') ||
                           error.message.includes('network') ||
                           error.message.includes('timeout') ||
                           error.name === 'TypeError';

      if (isNetworkError) {
        setIsOffline(true);

        if (lastSuccessfulLoadRef.current) {
          addLiveUpdate(
            `Offline - Using cached data from ${lastSuccessfulLoadRef.current.timestamp.toLocaleTimeString()}`,
            'warning',
            {}
          );
        }
      }

      onLoadError?.(error.message);
      throw error;
    }
  }, [addLiveUpdate, onLoadError]);

  // ==================== LOAD BULK STRATEGIES (Fallback) ====================
  const loadBulkStrategies = useCallback(async (force = false) => {
    if (!apiService.current || !mountedRef.current) return;

    console.log('[MONITOR-PERF] 🚀 Loading bulk strategies...');
    loadStartTime.current = performance.now();

    try {
      const result = await apiService.current.loadBulkStrategies(timeframeFilter, force);

      if (!mountedRef.current) return;

      if (result && Array.isArray(result.strategies)) {
        setBulkStrategies(result.strategies);
        setStatusCounts(result.statusCounts);
        setIsOffline(false);

        lastSuccessfulLoadRef.current = {
          strategies: result.strategies,
          statusCounts: result.statusCounts,
          timestamp: new Date(),
        };

        const totalTime = performance.now() - loadStartTime.current;
        console.log(`[MONITOR-PERF] ✅ Strategies loaded: ${totalTime.toFixed(2)}ms (${result.strategies.length})`);
      }
    } catch (error) {
      if (!mountedRef.current) return;

      console.error('[MONITOR-PERF] ❌ Load failed:', error);

      if (lastSuccessfulLoadRef.current) {
        setBulkStrategies(lastSuccessfulLoadRef.current.strategies);
        setStatusCounts(lastSuccessfulLoadRef.current.statusCounts);
        setIsOffline(true);
      }

      onLoadError?.(error.message);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [timeframeFilter, onLoadError]);

  // ==================== LOAD STRATEGY TRADES ====================
  const loadStrategyTrades = useCallback(async (bulkStrategyId) => {
    if (!mountedRef.current || strategyTrades[bulkStrategyId] || tradesLoading[bulkStrategyId]) {
      return;
    }

    if (!apiService.current) return;

    console.log(`[MONITOR-PERF] 📊 Loading trades for ${bulkStrategyId}...`);
    const startTime = performance.now();

    setTradesLoading(prev => ({ ...prev, [bulkStrategyId]: true }));

    try {
      const tradeData = await apiService.current.loadStrategyTrades(bulkStrategyId);

      if (!mountedRef.current) return;

      if (tradeData) {
        setStrategyTrades(prev => ({
          ...prev,
          [bulkStrategyId]: tradeData,
        }));

        const totalTime = performance.now() - startTime;
        console.log(`[MONITOR-PERF] ✅ Trades loaded: ${totalTime.toFixed(2)}ms (${tradeData.trades?.length || 0})`);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error(`[MONITOR-PERF] ❌ Trades load failed for ${bulkStrategyId}:`, error);
    } finally {
      if (mountedRef.current) {
        setTradesLoading(prev => ({ ...prev, [bulkStrategyId]: false }));
      }
    }
  }, [strategyTrades, tradesLoading]);

  // ==================== LOAD VALIDATED SEQUENCES ====================
  const loadValidatedSequences = useCallback(async (bulkStrategyId, force = false) => {
    if (!mountedRef.current) return;

    if (!force && strategyBroadcasts[bulkStrategyId]?.validated) {
      return;
    }

    console.log(`[MONITOR-PERF] 📻 Loading broadcasts for ${bulkStrategyId}...`);
    const startTime = performance.now();

    setBroadcastsLoading(prev => ({ ...prev, [bulkStrategyId]: true }));

    try {
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategies/${bulkStrategyId}/validated-broadcasts/`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!mountedRef.current) return;

      if (data.success && data.broadcasts) {
        setStrategyBroadcasts(prev => ({
          ...prev,
          [bulkStrategyId]: {
            broadcasts: data.broadcasts,
            validated: true,
            is_historical: true,
            data_age: `${data.lookback_days} days`,
          },
        }));

        const totalTime = performance.now() - startTime;
        console.log(`[MONITOR-PERF] ✅ Broadcasts loaded: ${totalTime.toFixed(2)}ms (${data.broadcasts?.length || 0})`);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error(`[MONITOR-PERF] ❌ Broadcasts load failed for ${bulkStrategyId}:`, error);
    } finally {
      if (mountedRef.current) {
        setBroadcastsLoading(prev => ({ ...prev, [bulkStrategyId]: false }));
      }
    }
  }, [apiBaseUrl, strategyBroadcasts]);

  // ==================== HANDLERS ====================
  const handleViewTradeHistory = useCallback((bulkStrategyId) => {
    if (!strategyTrades[bulkStrategyId]) {
      loadStrategyTrades(bulkStrategyId);
    }
    navigate(`/officer/trade-history?strategy=${bulkStrategyId}`);
  }, [strategyTrades, loadStrategyTrades, navigate]);

  const handleManualRefresh = useCallback(async () => {
    console.log('[MONITOR-PERF] 🔄 Manual refresh triggered');
    setIsOffline(false);
    dataCache.current.clear();

    setLoading(true);

    try {
      // Try analyzer endpoint first
      await loadCurrentState(true);
    } catch (error) {
      // Fallback to bulk strategies
      console.log('[MONITOR-PERF] Falling back to bulk strategies endpoint...');
      await loadBulkStrategies(true);
    } finally {
      setLoading(false);
    }
  }, [loadCurrentState, loadBulkStrategies]);

  const handleManualSync = useCallback(async () => {
    const strategiesToSync = Array.isArray(bulkStrategies) ? bulkStrategies : [];

    if (!apiService.current || strategiesToSync.length === 0) {
      setSyncStatus({ type: 'warning', message: 'No strategies to sync' });
      return;
    }

    if (isOffline) {
      setSyncStatus({ type: 'error', message: 'Cannot sync while offline' });
      return;
    }

    console.log(`[MONITOR-PERF] 🔄 Syncing ${strategiesToSync.length} strategies...`);
    const startTime = performance.now();

    setSyncing(true);
    setSyncStatus({ type: 'info', message: `Syncing ${strategiesToSync.length} strategies...` });

    try {
      const promises = strategiesToSync.map(strategy =>
        apiService.current.syncStrategy(strategy.bulk_strategy_id)
      );

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.success).length;

      const totalTime = performance.now() - startTime;

      if (!mountedRef.current) return;

      console.log(`[MONITOR-PERF] ✅ Sync complete: ${totalTime.toFixed(2)}ms`);

      setSyncStatus({
        type: successCount === strategiesToSync.length ? 'success' : 'warning',
        message: `Synced ${successCount}/${strategiesToSync.length} strategies in ${totalTime.toFixed(0)}ms`,
      });

      setLastSync(new Date());

      // Reload current state after sync
      await loadCurrentState(true);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('[MONITOR-PERF] ❌ Sync failed:', error);
      setSyncStatus({ type: 'error', message: `Sync failed: ${error.message}` });
    } finally {
      if (mountedRef.current) {
        setSyncing(false);
      }
    }
  }, [bulkStrategies, loadCurrentState, isOffline]);

  const handleComputeAll = useCallback(async () => {
    if (!apiService.current || isOffline) {
      addLiveUpdate('Cannot compute states while offline', 'error', {});
      return;
    }

    console.log('[MONITOR-PERF] 🔄 Computing all strategies...');
    const startTime = performance.now();

    setLoading(true);
    addLiveUpdate('Fetching current state from analyzer (will broadcast)...', 'info', {});

    try {
      const result = await loadCurrentState(true);

      const totalTime = performance.now() - startTime;
      console.log(`[MONITOR-PERF] ✅ Compute complete: ${totalTime.toFixed(2)}ms`);

      if (result?.summary) {
        addLiveUpdate(
          `✅ Loaded ${result.summary.pending} pending, ${result.summary.open} open strategies`,
          'success',
          {}
        );
      }
    } catch (error) {
      console.error('[MONITOR-PERF] ❌ Compute failed:', error);
      addLiveUpdate(`Compute failed: ${error.message}`, 'error', {});
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [isOffline, addLiveUpdate, loadCurrentState]);

  const handleCardExpand = useCallback((strategyId) => {
    setExpandedCards(prev => ({
      ...prev,
      [strategyId]: !prev[strategyId],
    }));

    if (!strategyBroadcasts[strategyId] && !isOffline) {
      loadValidatedSequences(strategyId, false);
    }
  }, [strategyBroadcasts, loadValidatedSequences, isOffline]);

  // ==================== CLEAR FILTERS ====================
  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setStrategyTypeFilter('all');
    setDependencyFilter('all');
    setDirectionFilter('all');
    setTimeframeFilter('all');
    setPositionFilter(false);
  }, []);

  const hasActiveFilters = searchQuery || strategyTypeFilter !== 'all' || dependencyFilter !== 'all' || directionFilter !== 'all' || timeframeFilter !== 'all' || positionFilter;

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    const initializeMonitor = async () => {
      if (!mountedRef.current) return;

      console.log('[MONITOR-PERF] 🚀 Initializing monitor...');
      const startTime = performance.now();

      setLoading(true);

      try {
        // Try analyzer endpoint first
        await loadCurrentState(true);
      } catch (error) {
        console.log('[MONITOR-PERF] Analyzer failed, falling back to bulk strategies...');
        await loadBulkStrategies(true);
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          const initTime = performance.now() - startTime;
          console.log(`[MONITOR-PERF] ✅ Initialization complete: ${initTime.toFixed(2)}ms`);
        }
      }
    };

    initializeMonitor();

    return () => {
      dataCache.current.clear();
    };
  }, [loadCurrentState, loadBulkStrategies]);

  // ==================== PERIODIC REFRESH ====================
  useEffect(() => {
    if (isOffline || !isConnected) return;

    const refreshInterval = setInterval(() => {
      if (mountedRef.current && !loading) {
        console.log('[MONITOR-PERF] 🔄 Periodic refresh...');
        loadCurrentState(false).catch(() => {
          // Silent fail for periodic refresh
        });
      }
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [isOffline, isConnected, loading, loadCurrentState]);

  // ==================== HELPER FUNCTIONS ====================
  const isGStrategy = (strategyId) => {
    return strategyId?.startsWith('G-') || false;
  };

  const hasDependency = (strategy) => {
    const dependsOn = strategy.depends_on || strategy.strategy_config?.depends_on;
    return dependsOn && dependsOn.enabled === true;
  };

  // ==================== FILTERED STRATEGIES ====================
  const filteredStrategies = useMemo(() => {
    const strategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];

    // First, filter out hidden strategies (is_visible = false)
    let filtered = strategies.filter(s => s.is_visible !== false);

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(s => {
        const id = (s.bulk_strategy_id || '').toLowerCase();
        const name = (s.name || '').toLowerCase();
        const symbol = (s.symbol || '').toLowerCase();
        return id.includes(query) || name.includes(query) || symbol.includes(query);
      });
    }

    // Strategy type filter (G vs non-G)
    if (strategyTypeFilter === 'g-only') {
      filtered = filtered.filter(s => isGStrategy(s.bulk_strategy_id));
    } else if (strategyTypeFilter === 'non-g') {
      filtered = filtered.filter(s => !isGStrategy(s.bulk_strategy_id));
    }

    // Dependency filter
    if (dependencyFilter === 'with-dep') {
      filtered = filtered.filter(s => hasDependency(s));
    } else if (dependencyFilter === 'no-dep') {
      filtered = filtered.filter(s => !hasDependency(s));
    }

    // Direction filter
    if (directionFilter !== 'all') {
      filtered = filtered.filter(s => {
        const direction = s.direction || s.strategy_config?.direction || 'LONG';
        return direction.toUpperCase() === directionFilter;
      });
    }

    // Filter by timeframe
    if (timeframeFilter !== 'all') {
      filtered = filtered.filter(s => s.timeframe === timeframeFilter);
    }

    // Filter by position (100% complete)
    if (positionFilter) {
      filtered = filtered.filter(s => {
        const progress = s.progress?.total > 0
          ? (s.progress.completed / s.progress.total) * 100
          : s.progress?.percentage || 0;
        return progress === 100;
      });
    }

    // Sort alphabetically
    return filtered.sort((a, b) => {
      const nameA = (a.name || a.bulk_strategy_id || '').toLowerCase();
      const nameB = (b.name || b.bulk_strategy_id || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [bulkStrategies, searchQuery, strategyTypeFilter, dependencyFilter, directionFilter, timeframeFilter, positionFilter]);

  // ==================== TIMEFRAME GROUPS ====================
  const timeframeGroups = useMemo(() => {
    const groups = {};
    const strategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];

    // Only include visible strategies
    strategies.filter(s => s.is_visible !== false).forEach(strategy => {
      const tf = strategy.timeframe || 'unknown';
      if (!groups[tf]) {
        groups[tf] = [];
      }
      groups[tf].push(strategy);
    });

    return groups;
  }, [bulkStrategies]);

  // ==================== STRATEGY TYPE COUNTS ====================
  const strategyCounts = useMemo(() => {
    const allStrategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    // Only count visible strategies
    const strategies = allStrategies.filter(s => s.is_visible !== false);
    
    return {
      gStrategies: strategies.filter(s => isGStrategy(s.bulk_strategy_id)).length,
      nonGStrategies: strategies.filter(s => !isGStrategy(s.bulk_strategy_id)).length,
      withDependency: strategies.filter(s => hasDependency(s)).length,
      withoutDependency: strategies.filter(s => !hasDependency(s)).length,
      longStrategies: strategies.filter(s => {
        const direction = s.direction || s.strategy_config?.direction || 'LONG';
        return direction.toUpperCase() === 'LONG';
      }).length,
      shortStrategies: strategies.filter(s => {
        const direction = s.direction || s.strategy_config?.direction || 'LONG';
        return direction.toUpperCase() === 'SHORT';
      }).length,
    };
  }, [bulkStrategies]);

  // ==================== SUMMARY STATISTICS ====================
  const summary = useMemo(() => {
    const allStrategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    // Only include visible strategies
    const visibleStrategies = allStrategies.filter(s => s.is_visible !== false);

    const strategies = timeframeFilter === 'all'
      ? visibleStrategies
      : visibleStrategies.filter(s => s.timeframe === timeframeFilter);

    const with100Percent = strategies.filter(s => {
      const progress = s.progress?.total > 0
        ? (s.progress.completed / s.progress.total) * 100
        : s.progress?.percentage || 0;
      return progress === 100;
    }).length;

    // ✅ FIX: Derive open/pending counts from live strategy data first,
    // only fall back to analyzerSummary if strategies haven't loaded yet
    const liveOpenCount = strategies.filter(s => s.current_state?.status === 'open').length;
    const livePendingCount = strategies.filter(s => s.current_state?.status === 'pending').length;

    return {
      total: strategies.length,
      total_all_timeframes: visibleStrategies.length,
      active: strategies.filter(s => s.is_active).length,
      in_position: with100Percent,
      pending: livePendingCount || analyzerSummary?.pending || 0,
      open: liveOpenCount || analyzerSummary?.open || 0,
      completed_strategies: strategies.reduce((sum, s) => sum + (s.progress?.completed || 0), 0),
      total_sub_strategies: strategies.reduce((sum, s) => sum + (s.progress?.total || 0), 0),
      timeframes: Object.keys(timeframeGroups).sort(),
      with_data: strategies.filter(s => s.has_data !== false).length,
      without_data: strategies.filter(s => s.has_data === false).length,
    };
  }, [bulkStrategies, timeframeFilter, timeframeGroups, analyzerSummary]);

  const strategiesCount = summary.total_all_timeframes;

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          position: 'relative',
          zIndex: 0,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} sx={{ color: '#764ba2' }} />
          <Typography sx={{ color: alpha('#fff', 0.5) }}>
            Loading strategies...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // ==================== MAIN RENDER ====================
  return (
    <Box 
      sx={{ 
        p: 3, 
        minHeight: '100vh',
        position: 'relative',
        zIndex: 0,
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: alpha('#1a1f2e', 0.8),
          border: `1px solid ${alpha(isOffline ? '#ff4757' : '#00d4ff', 0.2)}`,
          borderRadius: 2,
          position: 'relative',
          zIndex: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: alpha('#764ba2', 0.2),
                border: `1px solid ${alpha('#764ba2', 0.3)}`,
              }}
            >
              <TimelineIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}
              >
                Bulk Strategy Monitor
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Chip
                  icon={<DotIcon sx={{ fontSize: 12 }} />}
                  label={isConnected ? 'LIVE' : 'OFFLINE'}
                  size="small"
                  sx={{
                    bgcolor: alpha(isConnected ? '#00ff88' : '#ff4757', 0.15),
                    color: isConnected ? '#00ff88' : '#ff4757',
                    fontWeight: 600,
                    height: 22,
                  }}
                />
                {isOffline && (
                  <Chip
                    icon={<WifiOffIcon sx={{ fontSize: 12 }} />}
                    label="NO INTERNET"
                    size="small"
                    sx={{
                      bgcolor: alpha('#ff4757', 0.15),
                      color: '#ff4757',
                      fontWeight: 600,
                      height: 22,
                    }}
                  />
                )}
                {syncing && (
                  <Chip
                    icon={<CircularProgress size={12} sx={{ color: '#f59e0b' }} />}
                    label="SYNCING"
                    size="small"
                    sx={{
                      bgcolor: alpha('#f59e0b', 0.15),
                      color: '#f59e0b',
                      fontWeight: 600,
                      height: 22,
                    }}
                  />
                )}
                {analyzerSummary && (
                  <Chip
                    icon={<BoltIcon sx={{ fontSize: 14 }} />}
                    label={`${analyzerSummary.pending || 0} PENDING | ${analyzerSummary.open || 0} OPEN`}
                    size="small"
                    sx={{
                      bgcolor: alpha('#10b981', 0.15),
                      color: '#10b981',
                      fontWeight: 600,
                      height: 22,
                    }}
                  />
                )}
                {statusCounts && (
                  <Tooltip title={`${statusCounts.has_data} with data`}>
                    <Chip
                      icon={<StorageIcon sx={{ fontSize: 14 }} />}
                      label={`${statusCounts.has_data}/${strategiesCount} DATA`}
                      size="small"
                      sx={{
                        bgcolor: alpha('#764ba2', 0.15),
                        color: '#764ba2',
                        fontWeight: 600,
                        height: 22,
                      }}
                    />
                  </Tooltip>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha('#fff', 0.5),
                    fontSize: '0.75rem',
                  }}
                >
                  {strategiesCount} strategies ({summary.timeframes.join(', ')}) • 
                  NY: <strong style={{ color: '#00d4ff' }}>{nyCurrentTime}</strong> • 
                  Last sync: {lastSync?.toLocaleTimeString() || 'Never'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Tooltip title="View trade history">
              <Button
                variant="outlined"
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() => navigate('/officer/trade-history')}
                sx={{
                  borderColor: alpha('#00d4ff', 0.3),
                  color: '#00d4ff',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#00d4ff',
                    bgcolor: alpha('#00d4ff', 0.1),
                  },
                }}
              >
                History
              </Button>
            </Tooltip>

            <Tooltip title="Load current state from analyzer & broadcast">
              <Button
                variant="outlined"
                size="small"
                startIcon={<BoltIcon />}
                onClick={handleComputeAll}
                disabled={loading || isOffline}
                sx={{
                  borderColor: alpha('#10b981', 0.3),
                  color: '#10b981',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#10b981',
                    bgcolor: alpha('#10b981', 0.1),
                  },
                  '&:disabled': {
                    borderColor: alpha('#666', 0.2),
                    color: '#666',
                  },
                }}
              >
                Analyze All
              </Button>
            </Tooltip>

            <Tooltip title={isOffline ? 'Cannot sync while offline' : 'Sync from database'}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleManualSync}
                  disabled={syncing || isOffline}
                  sx={{
                    bgcolor: alpha(isOffline ? '#666' : '#f59e0b', 0.15),
                    border: `1px solid ${alpha(isOffline ? '#666' : '#f59e0b', 0.3)}`,
                    '&:hover': {
                      bgcolor: alpha(isOffline ? '#666' : '#f59e0b', 0.25),
                    },
                    '&:disabled': {
                      bgcolor: alpha('#666', 0.1),
                    },
                  }}
                >
                  <SyncIcon sx={{ color: (syncing || isOffline) ? '#666' : '#f59e0b', fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleManualRefresh}
              sx={{
                borderColor: alpha('#764ba2', 0.3),
                color: '#764ba2',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#764ba2',
                  bgcolor: alpha('#764ba2', 0.1),
                },
              }}
            >
              {isOffline ? 'Retry' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Search & Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: alpha('#1a1f2e', 0.6),
          border: `1px solid ${alpha('#00d4ff', 0.15)}`,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2}>
          {/* Row 1: Search + Direction + Timeframe */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search strategies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: alpha('#fff', 0.5), fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon sx={{ fontSize: 16, color: alpha('#fff', 0.5) }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha('#0a0e17', 0.6),
                  '& fieldset': {
                    borderColor: alpha('#00d4ff', 0.2),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha('#00d4ff', 0.4),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00d4ff',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#fff',
                  '&::placeholder': {
                    color: alpha('#fff', 0.4),
                  },
                },
              }}
            />

            <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.1) }} />

            {/* Direction Filter */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontWeight: 600 }}>
                Direction:
              </Typography>
              <ToggleButtonGroup
                value={directionFilter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) {
                    setDirectionFilter(newFilter);
                  }
                }}
                size="small"
                sx={{
                  bgcolor: alpha('#1a1f2e', 0.6),
                  '& .MuiToggleButton-root': {
                    color: alpha('#fff', 0.5),
                    border: `1px solid ${alpha('#00d4ff', 0.2)}`,
                    px: 1.5,
                    py: 0.5,
                    '&.Mui-selected': {
                      fontWeight: 700,
                    },
                    '&.Mui-selected[value="all"]': {
                      bgcolor: alpha('#00d4ff', 0.2),
                      color: '#00d4ff',
                    },
                    '&.Mui-selected[value="LONG"]': {
                      bgcolor: alpha('#00ff88', 0.2),
                      color: '#00ff88',
                    },
                    '&.Mui-selected[value="SHORT"]': {
                      bgcolor: alpha('#ff4757', 0.2),
                      color: '#ff4757',
                    },
                  },
                }}
              >
                <ToggleButton value="all">
                  BOTH ({strategyCounts.longStrategies + strategyCounts.shortStrategies})
                </ToggleButton>
                <ToggleButton value="LONG">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#00ff88' }} />
                    <span>LONG ({strategyCounts.longStrategies})</span>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="SHORT">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff4757' }} />
                    <span>SHORT ({strategyCounts.shortStrategies})</span>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.1) }} />

            {/* Timeframe Filter */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontWeight: 600 }}>
                Timeframe:
              </Typography>
              <ToggleButtonGroup
                value={timeframeFilter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) {
                    setTimeframeFilter(newFilter);
                  }
                }}
                size="small"
                sx={{
                  bgcolor: alpha('#1a1f2e', 0.6),
                  '& .MuiToggleButton-root': {
                    color: alpha('#fff', 0.5),
                    border: `1px solid ${alpha('#764ba2', 0.2)}`,
                    px: 1,
                    py: 0.5,
                    minWidth: 40,
                    '&.Mui-selected': {
                      bgcolor: alpha('#764ba2', 0.2),
                      color: '#764ba2',
                      fontWeight: 700,
                    },
                  },
                }}
              >
                <ToggleButton value="all">ALL</ToggleButton>
                {Object.keys(timeframeGroups).sort((a, b) => {
                  // Sort timeframes: seconds first, then minutes, then hours
                  const order = ['1s', '5s', '10s', '15s', '30s', '45s', '1m', '2m', '3m', '5m', '10m', '15m', '30m', '1h', '2h', '4h', '1D'];
                  return order.indexOf(a) - order.indexOf(b);
                }).map(tf => (
                  <ToggleButton key={tf} value={tf}>
                    <Badge 
                      badgeContent={timeframeGroups[tf]?.length || 0} 
                      color="secondary" 
                      max={99}
                      sx={{ 
                        '& .MuiBadge-badge': { 
                          fontSize: '0.65rem', 
                          height: 16, 
                          minWidth: 16,
                          right: -8,
                          top: -4
                        } 
                      }}
                    >
                      <span style={{ paddingRight: 4 }}>{tf}</span>
                    </Badge>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {/* Row 2: Strategy Type + Dependency + Position + Clear */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
            {/* Strategy Type Filter (G vs Non-G) */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontWeight: 600 }}>
                Type:
              </Typography>
              <ToggleButtonGroup
                value={strategyTypeFilter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) {
                    setStrategyTypeFilter(newFilter);
                  }
                }}
                size="small"
                sx={{
                  bgcolor: alpha('#1a1f2e', 0.6),
                  '& .MuiToggleButton-root': {
                    color: alpha('#fff', 0.5),
                    border: `1px solid ${alpha('#10b981', 0.2)}`,
                    px: 1.5,
                    py: 0.5,
                    '&.Mui-selected': {
                      bgcolor: alpha('#10b981', 0.2),
                      color: '#10b981',
                      fontWeight: 700,
                    },
                  },
                }}
              >
                <ToggleButton value="all">
                  <Tooltip title="All strategies">
                    <span>All</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="g-only">
                  <Tooltip title="G-strategies (parent/independent)">
                    <span>G-Only ({strategyCounts.gStrategies})</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="non-g">
                  <Tooltip title="Non-G strategies (may have dependencies)">
                    <span>Non-G ({strategyCounts.nonGStrategies})</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.1) }} />

            {/* Dependency Filter */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontWeight: 600 }}>
                Dependency:
              </Typography>
              <ToggleButtonGroup
                value={dependencyFilter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) {
                    setDependencyFilter(newFilter);
                  }
                }}
                size="small"
                sx={{
                  bgcolor: alpha('#1a1f2e', 0.6),
                  '& .MuiToggleButton-root': {
                    color: alpha('#fff', 0.5),
                    border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                    px: 1.5,
                    py: 0.5,
                    '&.Mui-selected': {
                      bgcolor: alpha('#f59e0b', 0.2),
                      color: '#f59e0b',
                      fontWeight: 700,
                    },
                  },
                }}
              >
                <ToggleButton value="all">
                  <Tooltip title="All strategies">
                    <span>All</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="with-dep">
                  <Tooltip title="Strategies with dependency on G-strategies">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <LinkIcon sx={{ fontSize: 14 }} />
                      <span>Linked ({strategyCounts.withDependency})</span>
                    </Stack>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="no-dep">
                  <Tooltip title="Strategies without dependencies">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <LinkOffIcon sx={{ fontSize: 14 }} />
                      <span>Independent ({strategyCounts.withoutDependency})</span>
                    </Stack>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.1) }} />

            {/* Position Filter (100%) */}
            <Tooltip title="Show only strategies at 100% completion">
              <ToggleButton
                value="position"
                selected={positionFilter}
                onChange={() => setPositionFilter(!positionFilter)}
                size="small"
                sx={{
                  color: positionFilter ? '#00ff88' : alpha('#fff', 0.5),
                  border: `1px solid ${alpha(positionFilter ? '#00ff88' : '#fff', 0.2)}`,
                  bgcolor: positionFilter ? alpha('#00ff88', 0.15) : 'transparent',
                  px: 1.5,
                  py: 0.5,
                  '&.Mui-selected': {
                    bgcolor: alpha('#00ff88', 0.2),
                    color: '#00ff88',
                  },
                }}
              >
                <BoltIcon sx={{ fontSize: 16, mr: 0.5 }} />
                100% Only
              </ToggleButton>
            </Tooltip>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{
                    color: '#ff4757',
                    borderColor: alpha('#ff4757', 0.3),
                    py: 0.5,
                    '&:hover': {
                      bgcolor: alpha('#ff4757', 0.1),
                      borderColor: '#ff4757',
                    },
                  }}
                >
                  Clear All
                </Button>
              </Tooltip>
            )}

            {/* Results Count - Push to right */}
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              variant="body2"
              sx={{ color: alpha('#fff', 0.6) }}
            >
              Showing <strong style={{ color: '#00d4ff' }}>{filteredStrategies.length}</strong> of {strategiesCount} strategies
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {isOffline && (
        <Alert
          severity="warning"
          icon={<WifiOffIcon />}
          sx={{
            mb: 3,
            bgcolor: alpha('#ff4757', 0.1),
            border: `1px solid ${alpha('#ff4757', 0.3)}`,
          }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Offline Mode</AlertTitle>
          {lastSuccessfulLoadRef.current ? (
            <>
              Network connection lost. Showing cached data from {lastSuccessfulLoadRef.current.timestamp.toLocaleTimeString()}.
              Click "Retry" to reconnect.
            </>
          ) : (
            <>No internet connection. Unable to load strategies.</>
          )}
        </Alert>
      )}

      {syncStatus && (
        <Collapse in={!!syncStatus}>
          <Alert
            severity={syncStatus.type}
            icon={syncStatus.type === 'success' ? <CheckCircleIcon /> : <WarningIcon />}
            onClose={() => setSyncStatus(null)}
            sx={{
              mb: 3,
              bgcolor: alpha(
                syncStatus.type === 'success' ? '#10b981' :
                syncStatus.type === 'error' ? '#ff4757' : '#f59e0b',
                0.1
              ),
              border: `1px solid ${alpha(
                syncStatus.type === 'success' ? '#10b981' :
                syncStatus.type === 'error' ? '#ff4757' : '#f59e0b',
                0.3
              )}`,
            }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>
              {syncStatus.type === 'success' ? 'Sync Complete' :
               syncStatus.type === 'error' ? 'Sync Failed' : 'Sync Status'}
            </AlertTitle>
            {syncStatus.message}
          </Alert>
        </Collapse>
      )}

      {statusCounts && (statusCounts.table_empty > 0 || statusCounts.table_missing > 0) && (
        <Alert
          severity="info"
          icon={<CloudOffIcon />}
          sx={{
            mb: 3,
            bgcolor: alpha('#00d4ff', 0.05),
            border: `1px solid ${alpha('#00d4ff', 0.2)}`,
          }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Some Strategies Have No Data</AlertTitle>
          {statusCounts.table_missing > 0 && (
            <Typography variant="body2">
              {statusCounts.table_missing} strategies have missing database tables.
            </Typography>
          )}
          {statusCounts.table_empty > 0 && (
            <Typography variant="body2">
              {statusCounts.table_empty} strategies have empty database tables.
            </Typography>
          )}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard
            title="Total"
            value={summary.total}
            color="#00d4ff"
            icon={DashboardIcon}
            subtitle={timeframeFilter === 'all' ? 'All Timeframes' : timeframeFilter}
            onClick={() => {
              setPositionFilter(false);
              setTimeframeFilter('all');
            }}
            isActive={!positionFilter && timeframeFilter === 'all'}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            title="Pending"
            value={summary.pending}
            color="#f59e0b"
            icon={ScheduleIcon}
            subtitle="Awaiting Confirmations"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            title="Open"
            value={summary.open}
            color="#00ff88"
            icon={BoltIcon}
            subtitle="In Position"
            onClick={() => setPositionFilter(!positionFilter)}
            isActive={positionFilter}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            title="Completed"
            value={summary.completed_strategies}
            color="#764ba2"
            icon={CloudDoneIcon}
            subtitle={`of ${summary.total_sub_strategies}`}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            title="Progress"
            value={`${
              summary.total_sub_strategies > 0
                ? ((summary.completed_strategies / summary.total_sub_strategies) * 100).toFixed(0)
                : 0
            }%`}
            color="#ffa502"
            icon={SpeedIcon}
            subtitle="Overall"
          />
        </Grid>
      </Grid>

      {/* Strategy Cards */}
      {filteredStrategies.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: alpha('#1a1f2e', 0.6),
            border: `1px dashed ${alpha('#00d4ff', 0.2)}`,
            borderRadius: 2,
          }}
        >
          <DashboardIcon
            sx={{ fontSize: 64, color: alpha('#fff', 0.1), mb: 2 }}
          />
          <Typography variant="h6" sx={{ color: alpha('#fff', 0.4), mb: 1 }}>
            No Strategies Found
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.3), mb: 2 }}>
            {searchQuery 
              ? `No strategies matching "${searchQuery}"`
              : hasActiveFilters 
              ? 'No strategies match the current filters'
              : 'Create a bulk strategy to get started'}
          </Typography>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{
                borderColor: alpha('#00d4ff', 0.3),
                color: '#00d4ff',
              }}
            >
              Clear Filters
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2} sx={{ justifyContent: 'flex-start' }}>
          {filteredStrategies.map((strategy) => (
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={4}
              key={strategy.bulk_strategy_id}
              sx={{
                flexBasis: {
                  md: '45%',
                  lg: '45%',
                  xl: '30%',
                },
                maxWidth: {
                  md: '45% !important',
                  lg: '45% !important',
                  xl: '30% !important',
                },
              }}
            >
              <BulkStrategyCard
                strategy={strategy}
                isExpanded={expandedCards[strategy.bulk_strategy_id] || false}
                onExpandChange={handleCardExpand}
                onOpenDoc={() => {}}
                onViewTradeHistory={handleViewTradeHistory}
                liveUpdates={liveUpdates}
                broadcasts={strategyBroadcasts[strategy.bulk_strategy_id]?.broadcasts || []}
                broadcastsLoading={broadcastsLoading[strategy.bulk_strategy_id] || false}
                isHistorical={strategyBroadcasts[strategy.bulk_strategy_id]?.is_historical || false}
                dataAge={strategyBroadcasts[strategy.bulk_strategy_id]?.data_age || '24 hours'}
                trades={strategyTrades[strategy.bulk_strategy_id]?.trades || []}
                tradesLoading={tradesLoading[strategy.bulk_strategy_id] || false}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default GenBulkStrategyMonitor;
